/**
 * Coverage Page (placeholder)
 *
 * Will surface per-source and per-workspace ingestion coverage metrics:
 * chunk counts, embedding freshness, scope overlap heat-maps, and
 * gap analysis for the shared/overlay retrieval model.
 *
 * Gap 8 from the v2 migration plan — full implementation deferred.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Map } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';

export const CoveragePage: React.FC = () => {
  usePageTitle('Coverage');
  return (
    <div className="space-y-5">
      {/* Header — mirrors My Workspaces / Jurisdiction header pattern */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coverage</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Ingestion coverage and scope overlap analysis for the shared/overlay
            retrieval model.
          </p>
        </div>
        <Badge variant="secondary">Coming Soon</Badge>
      </div>

      {/* Placeholder — matches empty-state pattern used across Coverage section */}
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20 text-center">
        <Map className="mb-4 h-12 w-12 text-gray-300" />
        <p className="text-base font-semibold text-gray-700">
          Not yet implemented
        </p>
        <p className="mt-1 text-sm text-gray-400 max-w-md">
          Will show per-source chunk coverage, embedding freshness, workspace
          scope overlap heat-maps, and gap analysis to help identify
          under-represented topics in your knowledge base.
        </p>
      </div>

      {/* Planned metrics grid — faded preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 opacity-40 pointer-events-none select-none">
        {[
          {
            label: 'Total Chunks',
            description: 'Indexed across all sources',
          },
          {
            label: 'Stale Embeddings',
            description: 'Chunks pending re-embedding',
          },
          {
            label: 'Scope Overlaps',
            description: 'Cross-workspace theme collisions',
          },
        ].map(({ label, description }) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-300">—</div>
              <p className="text-xs text-gray-400 mt-1">{description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
